/**
 * @from weberus/packages/feature-flag
 */
import { useContext } from 'react'

import { FeatureFlagContext } from './feature-flag-provider'

import type { FeatureFlagService } from './feature-flag-service'

/**
 * `useBaseFeatureFlag`는 기본 피처 플래그 관련 기능을 제공하는 훅입니다.
 * 각 프로젝트에서는 `useBaseFeatureFlag`를 직접 사용하는 대신, `useFeatureFlag`를 구현하여 사용하세요.
 *
 * @template K 피처 플래그 키의 타입. (문자열 타입의 서브타입)
 * @returns {FeatureFlagService<K>} FeatureFlagService 인스턴스를 반환
 *
 * @example
 * // 각 프로젝트에서 useFeatureFlag 훅을 구현하여 사용하세요.
 * import type { FeatureFlagService } from '@weberus/feature-flag/react';
 * import { useBaseFeatureFlag } from '@weberus/feature-flag/react';
 * import type { FeatureFlagKey } from '@/conf/feature-flag-config';
 *
 * export const useFeatureFlag = (): FeatureFlagService<FeatureFlagKey> => {
 *  return useBaseFeatureFlag<FeatureFlagKey>();
 * };
 *
 * // 사용 예시
 * const featureFlag = useFeatureFlag();
 * const isEnabled = featureFlag.isFeatureEnabled('feature-key');
 */
export const useBaseFeatureFlag = <K extends string>(): FeatureFlagService<K> => {
  const context = useContext(FeatureFlagContext)
  if (!context) {
    throw new Error('useFeatureFlag는 반드시 FeatureFlagProvider 내부에서 사용되어야 합니다.')
  }
  return context
}
